import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Spin } from 'antd'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import {
  fetchCurrentReceipt,
  fetchReceiptStats,
  fetchTerminalReceipts,
  type UkmReceipt,
  type UkmReceiptStats,
  type UkmReceiptSyncMeta,
} from './receiptApi'
import { useCashLive, type CashLiveEvent } from './useCashLive'
import styles from './CashPosFeed.module.css'

interface Props {
  terminalId: number
  enabled?: boolean
  /** Открыть архивный клип вокруг события; allTs — метки соседних действий в чеке. */
  onOpenArchiveMoment?: (eventTsUs: number, allEventTsUs: number[]) => void
  onAlertCountChange?: (count: number) => void
}

const FLASH_MS = 2000

function formatMoney(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '0,00'
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Подпись в ленте. Архив открывается по frame_ts_us, не по этой строке. */
function formatMskDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const raw = String(value).trim()
  const wall = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/)
  if (wall) {
    return `${wall[3]}.${wall[2]}.${wall[1]}, ${wall[4]}:${wall[5]}:${wall[6]}`
  }
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return raw
  return d.toLocaleString('ru-RU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function statusLabel(status: UkmReceipt['status']): string {
  switch (status) {
    case 'closed':
      return 'закрыт'
    case 'cancelled':
      return 'отменён'
    default:
      return 'открыт'
  }
}

function sideEventTitle(ev: UkmReceipt['side_events'][0]): string {
  const label = ev.type_label || ev.type
  const text = (ev.text || '').trim()
  if (!text) return label
  const pct = text.match(/\((-?\d+(?:[.,]\d+)?%)\)/)
  const num = text.replace(/\s*\([^)]*\)\s*$/, '').trim()
  if (pct && num && /DISCOUNT|BONUS/i.test(ev.type)) {
    return `${label}: ${num} · скидка ${pct[1]}`
  }
  return `${label}: ${text}`
}

function collectReceiptEventTs(r: UkmReceipt): number[] {
  const out: number[] = []
  for (const line of r.items) {
    if (line.frame_ts_us != null && Number.isFinite(line.frame_ts_us)) {
      out.push(Number(line.frame_ts_us))
    }
  }
  for (const ev of r.side_events) {
    if (ev.event_ts_us != null && Number.isFinite(ev.event_ts_us)) {
      out.push(Number(ev.event_ts_us))
    }
  }
  return out
}

function VideoBtn({
  ts,
  neighbors,
  onOpen,
}: {
  ts: number | null | undefined
  neighbors: number[]
  onOpen?: (eventTsUs: number, allEventTsUs: number[]) => void
}) {
  if (!onOpen || ts == null) return null
  return (
    <button
      type="button"
      className={styles.linkBtn}
      onClick={() => onOpen(ts, neighbors)}
      title="Архив: несколько секунд до и после события"
    >
      Видео
    </button>
  )
}

/** Пользователь смотрит закрытый/старый чек — не переключать на live. */
function isBusyLookingAtClosed(
  receipts: UkmReceipt[],
  expanded: Record<string, boolean>,
): boolean {
  for (const r of receipts) {
    if (!expanded[String(r.id)]) continue
    if (r.status !== 'open') return true
  }
  return false
}

export function CashPosFeed({
  terminalId,
  enabled = true,
  onOpenArchiveMoment,
  onAlertCountChange,
}: Props) {
  const [receipts, setReceipts] = useState<UkmReceipt[]>([])
  const [stats, setStats] = useState<UkmReceiptStats | null>(null)
  const [sync, setSync] = useState<UkmReceiptSyncMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [flashIds, setFlashIds] = useState<Record<string, boolean>>({})

  const knownIdsRef = useRef<Set<number>>(new Set())
  const flashTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const listRef = useRef<HTMLUListElement>(null)
  const seededRef = useRef(false)
  const expandedRef = useRef(expanded)
  expandedRef.current = expanded

  // Смена кассы — сброс состояния раскрытия/вспышек
  useEffect(() => {
    knownIdsRef.current = new Set()
    seededRef.current = false
    setExpanded({})
    setFlashIds({})
    for (const t of flashTimersRef.current.values()) clearTimeout(t)
    flashTimersRef.current.clear()
  }, [terminalId])

  const flashReceipt = useCallback((id: number) => {
    const key = String(id)
    setFlashIds((prev) => ({ ...prev, [key]: true }))
    const prevTimer = flashTimersRef.current.get(id)
    if (prevTimer) clearTimeout(prevTimer)
    const timer = setTimeout(() => {
      setFlashIds((prev) => {
        if (!prev[key]) return prev
        const next = { ...prev }
        delete next[key]
        return next
      })
      flashTimersRef.current.delete(id)
    }, FLASH_MS)
    flashTimersRef.current.set(id, timer)
  }, [])

  const load = useCallback(
    async (silent = false) => {
      if (!enabled || !terminalId) return
      if (!silent) setLoading(true)
      setError(null)
      try {
        const [cur, list, st] = await Promise.all([
          fetchCurrentReceipt(terminalId),
          fetchTerminalReceipts(terminalId, { limit: 40 }),
          fetchReceiptStats(terminalId),
        ])
        setSync(cur.sync)
        setStats(st)

        const byId = new Map<number, UkmReceipt>()
        if (cur.receipt) byId.set(cur.receipt.id, cur.receipt)
        for (const r of list) byId.set(r.id, r)
        const merged = [...byId.values()].sort((a, b) =>
          (b.opened_at_msk || '').localeCompare(a.opened_at_msk || ''),
        )
        setReceipts(merged)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки чеков')
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [terminalId, enabled],
  )

  const onLiveEvent = useCallback((ev: CashLiveEvent) => {
    if (ev.type === 'receipt' && ev.receipt) {
      setReceipts((prev) => {
        const byId = new Map(prev.map((r) => [r.id, r]))
        byId.set(ev.receipt.id, ev.receipt)
        return [...byId.values()].sort((a, b) =>
          (b.opened_at_msk || '').localeCompare(a.opened_at_msk || ''),
        )
      })
      setError(null)
    } else if (ev.type === 'stats' && ev.stats) {
      setStats(ev.stats)
    } else if (ev.type === 'sync') {
      setSync(ev.sync)
    }
  }, [])

  useCashLive(terminalId, enabled, {
    onEvent: onLiveEvent,
    onError: () => {
      void load(true)
    },
  })

  useEffect(() => {
    void load(false)
  }, [load])

  const alertCount = useMemo(
    () => receipts.reduce((s, r) => s + (r.alert_count || 0), 0),
    [receipts],
  )

  useEffect(() => {
    onAlertCountChange?.(alertCount)
  }, [alertCount, onAlertCountChange])

  // Авто-раскрытие live + вспышка нового чека (не мешаем просмотру старых)
  useEffect(() => {
    if (!receipts.length) return

    const known = knownIdsRef.current
    const expandedNow = expandedRef.current
    const live = receipts.find((r) => r.status === 'open') ?? null
    const newIds: number[] = []
    for (const r of receipts) {
      if (!known.has(r.id)) newIds.push(r.id)
    }

    // Первый снимок: запомнить id, раскрыть live (или первый)
    if (!seededRef.current) {
      seededRef.current = true
      for (const r of receipts) known.add(r.id)
      const focusId = live?.id ?? receipts[0]?.id
      if (focusId != null) {
        setExpanded({ [String(focusId)]: true })
      }
      return
    }

    const busy = isBusyLookingAtClosed(receipts, expandedNow)

    for (const id of newIds) {
      known.add(id)
      const r = receipts.find((x) => x.id === id)
      if (r?.status === 'open') {
        flashReceipt(id)
      }
    }

    // Live появился/сменился — раскрыть, только если не смотрим старый чек
    if (live) {
      const liveKey = String(live.id)
      const alreadyOpen = !!expandedNow[liveKey]
      if (!busy && !alreadyOpen) {
        setExpanded((prev) => {
          const next: Record<string, boolean> = {}
          for (const r of receipts) {
            if (r.status === 'open') continue
            if (prev[String(r.id)]) next[String(r.id)] = true
          }
          next[liveKey] = true
          return next
        })
        requestAnimationFrame(() => {
          const el = listRef.current?.querySelector(`[data-receipt-id="${live.id}"]`)
          el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        })
      }
    }
  }, [receipts, flashReceipt])

  if (!enabled) return null

  const toggle = (id: number) => {
    const key = String(id)
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div className={styles.wrap}>
      {stats ? (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Чеков</span>
            <span className={styles.kpiValue}>{stats.receipt_count}</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Сумма</span>
            <span className={styles.kpiValue}>{formatMoney(stats.sum_closed)} ₽</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Средний</span>
            <span className={styles.kpiValue}>{formatMoney(stats.avg_closed)} ₽</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Алерты</span>
            <span className={styles.kpiValue}>{stats.alerts_linked}</span>
          </div>
        </div>
      ) : null}

      {sync?.last_error ? (
        <div className={styles.error} title={sync.last_error}>
          Чек с кассы:{' '}
          {/SSH|недоступна|handshake|ECONNREFUSED|ETIMEDOUT/i.test(sync.last_error)
            ? 'нет связи по SSH (VPN/сеть/IP). ActivePOS может быть актуальнее.'
            : sync.last_error}
        </div>
      ) : null}
      {error ? <div className={styles.error}>{error}</div> : null}

      {loading && !receipts.length ? (
        <div className={styles.empty}>
          <Spin size="small" />
        </div>
      ) : !receipts.length ? (
        <div className={styles.empty}>Пока нет чеков. Ждём данные с кассы УКМ-5.</div>
      ) : (
        <ul className={styles.receiptList} ref={listRef}>
          {receipts.map((r) => {
            const open = !!expanded[String(r.id)]
            const flashing = !!flashIds[String(r.id)]
            const eventTsList = collectReceiptEventTs(r)
            return (
              <li
                key={r.id}
                data-receipt-id={r.id}
                className={[
                  styles.receipt,
                  r.alert_count > 0 ? styles.receiptAlert : '',
                  r.status === 'open' ? styles.receiptOpen : '',
                  flashing ? styles.receiptFlash : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <button type="button" className={styles.receiptHead} onClick={() => toggle(r.id)}>
                  <span className={styles.chevron}>{open ? <DownOutlined /> : <RightOutlined />}</span>
                  <span className={styles.receiptMain}>
                    <span className={styles.receiptTitle}>
                      Чек № {r.receipt_number_in_shift || r.id}
                      {r.status === 'open' ? ' · live' : ''}
                      {flashing && r.status === 'open' ? (
                        <span className={styles.newBadge}>новый</span>
                      ) : null}
                    </span>
                    <span className={styles.receiptMeta}>
                      {formatMskDateTime(r.opened_at_msk)} · {statusLabel(r.status)} ·{' '}
                      {formatMoney(r.total)} ₽
                    </span>
                  </span>
                </button>

                {open ? (
                  <div className={styles.paper}>
                    {r.client_name || r.client_card || r.client_balance != null ? (
                      <section className={styles.paperSection}>
                        <div className={styles.paperSectionTitle}>Покупатель</div>
                        <div className={styles.paperClient}>
                          {[r.client_name, r.client_card ? `карта ${r.client_card}` : null]
                            .filter(Boolean)
                            .join(' · ')}
                          {r.client_balance != null
                            ? ` · баланс ${formatMoney(r.client_balance)} ₽`
                            : ''}
                        </div>
                      </section>
                    ) : null}

                    <section className={styles.paperSection}>
                      <div className={styles.paperSectionTitle}>Товары</div>
                      {r.items.length === 0 ? (
                        <div className={styles.paperMeta}>Нет позиций</div>
                      ) : (
                        r.items.map((line) => {
                          const metaBits = [
                            line.barcode ? `ШК ${line.barcode}` : null,
                            line.scu ? `АРТ ${line.scu}` : null,
                            line.taxes?.length
                              ? line.taxes
                                  .map(
                                    (t) =>
                                      `НДС${t.percent != null ? ` ${t.percent}%` : ''} ${formatMoney(t.amount)}`,
                                  )
                                  .join(', ')
                              : null,
                            line.discount > 0 ? `скидка −${formatMoney(line.discount)}` : null,
                          ].filter(Boolean)
                          return (
                            <div key={line.id} className={styles.paperItem}>
                              <div className={styles.paperItemHead}>
                                <span className={styles.paperItemName}>
                                  {line.line_no}. {line.name}
                                </span>
                                <span className={styles.paperItemSum}>
                                  {formatMoney(line.line_total)} ₽
                                </span>
                              </div>
                              <div className={styles.paperItemRow}>
                                <span className={styles.paperMeta}>
                                  {formatMoney(line.qty)} {line.measure || 'шт'} ×{' '}
                                  {formatMoney(line.price)} ₽
                                  {metaBits.length ? ` · ${metaBits.join(' · ')}` : ''}
                                </span>
                                <VideoBtn
                                  ts={line.frame_ts_us}
                                  neighbors={eventTsList}
                                  onOpen={onOpenArchiveMoment}
                                />
                              </div>
                            </div>
                          )
                        })
                      )}
                    </section>

                    {r.removed_items?.length ? (
                      <section className={styles.paperSection}>
                        <div className={styles.paperSectionTitle}>Удалённые</div>
                        {r.removed_items.map((item) => (
                          <div key={item.key} className={styles.paperItemRemoved}>
                            <span>{item.name}</span>
                            <span>{formatMoney(item.price)} ₽</span>
                          </div>
                        ))}
                      </section>
                    ) : null}

                    <div className={styles.paperTotals}>
                      <div className={styles.paperTotalLine}>
                        <span>Товаров</span>
                        <span>{r.items.length}</span>
                      </div>
                      {r.discount_total > 0 ? (
                        <div className={`${styles.paperTotalLine} ${styles.paperDisc}`}>
                          <span>Скидка</span>
                          <span>−{formatMoney(r.discount_total)} ₽</span>
                        </div>
                      ) : null}
                      {r.tax_total != null && r.tax_total > 0 ? (
                        <div className={styles.paperTotalLine}>
                          <span>НДС</span>
                          <span>{formatMoney(r.tax_total)} ₽</span>
                        </div>
                      ) : null}
                      <div className={`${styles.paperTotalLine} ${styles.paperFinal}`}>
                        <span>Итого</span>
                        <span>{formatMoney(r.total)} ₽</span>
                      </div>
                    </div>

                    {r.payments.length ? (
                      <section className={styles.paperSection}>
                        <div className={styles.paperSectionTitle}>Оплата</div>
                        {r.payments.map((p) => (
                          <div key={p.id} className={styles.paperPay}>
                            <span>{p.type || 'Оплата'}</span>
                            <span>{formatMoney(p.amount)} ₽</span>
                          </div>
                        ))}
                      </section>
                    ) : null}

                    {r.side_events.length ? (
                      <section className={styles.paperSection}>
                        <div className={styles.paperSectionTitle}>ActivePOS</div>
                        {r.side_events.map((ev) => (
                          <div
                            key={ev.id}
                            className={`${styles.paperSide} ${ev.is_alert ? styles.lineAlert : ''}`}
                          >
                            <span className={styles.paperSideText}>{sideEventTitle(ev)}</span>
                            <VideoBtn
                              ts={ev.event_ts_us}
                              neighbors={eventTsList}
                              onOpen={onOpenArchiveMoment}
                            />
                          </div>
                        ))}
                      </section>
                    ) : null}

                    {(r.state || r.global_doc_number) && (
                      <footer className={styles.paperFooter}>
                        {[
                          r.state ? `Статус: ${r.state}` : null,
                          r.global_doc_number ? `Док. № ${r.global_doc_number}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </footer>
                    )}
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
